/**
 * columnar.js — Columnar JSON encode/decode helpers.
 * AI: Added by Claude Sonnet 4.6 (2026-05-25, sstein) — client-side counterpart to
 *     utilities.py to_columnar / from_columnar. Provides encode, decode, and a
 *     one-level-deep response post-processor for use in DataClass (data.js).
 *
 * Wire format:
 *   Plain   → [{"a": 1, "b": 2}, {"a": 3, "b": 4}]
 *   Columnar→ {"__columnar": 1, "c": ["a", "b"], "r": [[1, 2], [3, 4]]}
 *
 * The "__columnar" sentinel makes the encoding self-describing so the client can detect
 * and expand it automatically without any per-blade configuration.
 */

// ── Encode ────────────────────────────────────────────────────────────────────

/**
 * Convert a plain array of uniform objects into columnar format.
 *
 * Typically used only in test/tooling contexts on the JS side. Production encoding
 * is performed server-side via utilities.py to_columnar() before the AResponse.
 *
 * @param   {Object[]} records  Array of plain objects with identical key sets.
 *                              Key order is taken from the first element.
 * @returns {Object}            { __columnar: 1, c: [...cols], r: [[...vals], ...] }
 *                              Returns { __columnar: 1, c: [], r: [] } for empty input.
 */
function toColumnar(records) {
    if (!records || records.length === 0) {
        return { __columnar: 1, c: [], r: [] };
    }
    const cols = Object.keys(records[0]);
    const rows = records.map(rec => cols.map(col => (col in rec ? rec[col] : null)));
    return { __columnar: 1, c: cols, r: rows };
}

// ── Decode ────────────────────────────────────────────────────────────────────

/**
 * Expand a columnar-encoded object back into a plain array of objects.
 *
 * @param   {Object} data  Must have "c" (column name array) and "r" (value arrays).
 *                         The "__columnar" sentinel key is tolerated but not required.
 * @returns {Object[]}     Reconstructed plain array. Returns [] if c/r are empty.
 */
function fromColumnar(data) {
    const cols = data.c || [];
    const rows = data.r || [];
    return rows.map(row => {
        const obj = {};
        cols.forEach((col, i) => { obj[col] = row[i] !== undefined ? row[i] : null; });
        return obj;
    });
}

// ── Response post-processor ───────────────────────────────────────────────────

/**
 * Scan one level deep into an API response data object and expand any columnar-encoded
 * fields in-place. Also handles the case where the root data value itself is columnar.
 *
 * Depth is intentionally limited to one level for performance safety. The common
 * production pattern is a top-level response key holding a columnar list:
 *     { "items": { __columnar: 1, c: [...], r: [...] }, "summary": {...} }
 *
 * Called automatically by DataClass after a successful API response, before dispatching
 * to the blade's success callback. No blade-level code changes are required.
 *
 * @param   {*} data  The .data payload from an AResponse. May be any type.
 * @returns {*}       The same value, with any columnar fields expanded.
 */
function expandColumnarFields(data) {
    // Root is columnar (endpoint returned a bare columnar list)
    if (data !== null && typeof data === 'object' && data.__columnar === 1) {
        return fromColumnar(data);
    }

    // Walk one level deep for named fields
    if (data !== null && typeof data === 'object' && !Array.isArray(data)) {
        Object.keys(data).forEach(key => {
            const val = data[key];
            if (val !== null && typeof val === 'object' && val.__columnar === 1) {
                data[key] = fromColumnar(val);
            }
        });
    }

    return data;
}

// ── Deep recursive decoder ────────────────────────────────────────────────────

/**
 * Recursively walk any nested structure (object, array, or scalar) and expand every
 * { "__columnar": 1, ... } sentinel found at any depth.
 *
 * Mirrors Python _deep_from_columnar() in portal/mods/utilities.py.
 * Called by DataClass.onQuerySuccess / onQueryError to transparently expand wire data
 * before dispatching to blade callbacks.  No per-blade code changes are required.
 *
 * Prefer this over expandColumnarFields() for production use — it handles nested
 * columnar values produced by endpoints that encode multiple lists in one response.
 *
 * @param   {*} data  Any value — plain object, array, primitive, null, or undefined.
 * @returns {*}       Same structure with all __columnar sentinels expanded.
 */
// AI: Added by Claude Sonnet 4.6 (2026-05-26, sstein) — full recursive deep expansion; mirrors Python _deep_from_columnar()
function gwDeepFromColumnar(data) {
    if (data === null || data === undefined) {
        return data;
    }
    if (typeof data !== 'object') {
        return data;
    }
    if (Array.isArray(data)) {
        return data.map(gwDeepFromColumnar);
    }
    // Plain object — check for the columnar sentinel first
    if (data.__columnar === 1) {
        return fromColumnar(data).map(gwDeepFromColumnar);
    }
    // Otherwise recurse into every value
    var result = {};
    Object.keys(data).forEach(function(k) {
        result[k] = gwDeepFromColumnar(data[k]);
    });
    return result;
}
